import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { CursorProvider } from './CursorContext';
import { ToastContainer } from 'react-toastify';
import TopPanelAdmin from '@CommonFunctionalDisplay/TopPanelAdmin';
import SidePanelAdmin from '@CommonFunctionalDisplay/SidePanelAdmin';
import useThemeChange from '@Hooks/useThemeChange';
import useSessionCheck from '@Hooks/useSessionCheck';
import { useReduxToken } from '@Hooks/reduxTokenHook/useReduxToken';
import { useNavigateWithRedirect } from '@Hooks/useNavigateWithRedirect';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import { useMediaQuery, useTheme } from '@mui/material';
import './AuthLayout.scss';

const AuthLayout = () => {

    const location = useLocation();
    const navigate = useNavigateWithRedirect();
    const { setToken, token } = useReduxToken();
    const { activeUserId } = useReduxActiveUser();
    const { setCurrentOrg } = useReduxOrg();
    const { currentProject, setCurrentProject } = useReduxProject();
    const userAuthService = new UserAuthService();
    const theme = useTheme();
    useMediaQuery(theme.breakpoints.down('lg'));
    const className = 'authLayout';

    useThemeChange();
    useSessionCheck();

    useEffect(()=> {
        if(!token || token === 'void') {
            navigate('/logout');
        } else {
            if(!currentProject) {
                checkProjectAndOrg();
            }
        }
    }, []);

    const checkProjectAndOrg = async()=> {
        let accessList = await userAuthService.getAccessListForUser(activeUserId);
        accessList = JSON.parse(accessList.body);
        if(accessList?.orgs?.length > 0) {
            let activeOrg = accessList.orgs.find(org => org.visibility == 'resolve');
            if(!!activeOrg) {
                setCurrentOrg(activeOrg);
            } else {
                navigate('/dashboard/onboarding');
                return;
            }
        }
        if(accessList?.projects?.length > 0) {
            let activeProject = accessList.projects.find(project => project.visibility == 'resolve');
            if(!!activeProject) {
                setCurrentProject(activeProject);
            } else {
                navigate('/dashboard/onboarding');
                return;
            }
        }
        navigate('/dashboard/onboarding');
        return;
    }

    window.addEventListener('error', e => {
        if (e.message === 'ResizeObserver loop limit exceeded') {
            const resizeObserverErrDiv = document.getElementById(
                'webpack-dev-server-client-overlay-div'
            );
            const resizeObserverErr = document.getElementById(
                'webpack-dev-server-client-overlay'
            );
            if (resizeObserverErr) {
                resizeObserverErr.setAttribute('style', 'display: none');
            }
            if (resizeObserverErrDiv) {
                resizeObserverErrDiv.setAttribute('style', 'display: none');
            }
        }
    });

    window.addEventListener('error', e => {
        if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
            const resizeObserverErrDiv = document.getElementById(
                'webpack-dev-server-client-overlay-div'
            );
            const resizeObserverErr = document.getElementById(
                'webpack-dev-server-client-overlay'
            );
            if (resizeObserverErr) {
                resizeObserverErr.setAttribute('style', 'display: none');
            }
            if (resizeObserverErrDiv) {
                resizeObserverErrDiv.setAttribute('style', 'display: none');
            }
        }
    });

    if(!token || token === 'void') {
        if(token === 'void') {
            setToken({'token': null});
            return <Navigate to='/login' state={{ from: location }} replace />
        }
        return (
            <>
                <ToastContainer
                    limit={1}
                    containerId={'login'}
                    position='top-center'
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss={false}
                    draggable
                    pauseOnHover
                    theme='light'/>
                    <Navigate to='/login' state={{ from: location }} replace />
            </>
        );
    }

    return (
        <>
            <ToastContainer
                limit={1}
                containerId={'error'}
                position='bottom-left'
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss={false}
                draggable
                pauseOnHover
                theme='light'/>
                <CursorProvider>
                    <div className={className}>
                        <div className={className + '__main'}>
                            {
                                location.pathname !== '/onboarding' &&
                                    <>
                                        <TopPanelAdmin />
                                        <SidePanelAdmin />
                                    </>
                            }
                            <div className={className + '__main__section'} style={{ flex: 1 }}>
                                <Outlet />
                            </div>
                        </div>
                    </div>
                </CursorProvider>

        </>
    );
};

export default AuthLayout;
